import React, { useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form';
import BasicDrawer from '../../components/Drawer';
import { Col, ConfigProvider, Row, TimePicker } from 'antd';
import { ApiCaller, Colors, Utils } from '../../config';
import dayjs from 'dayjs';
import { Button } from '../../components';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { AttendenceManagementAction } from '../../store/actions';
import useColors from '../../hooks/useColors';
import { localToUtc, utcToLocal } from '../../config/util/dateTimeTemporal';

const EditTimeDrawer = ({
    drawerStates,
    setDrawerStates,
    editTimeData,
    filterStates,
    setFilterStates,
    onAfterSave,  // v2 callback: called after successful save instead of re-dispatching old action
}) => {
    const { colorPrimary } = useColors();
    const timeFormat = useSelector((s) => s.AppReducer?.timeFormat) || 'hh:mm A';
    const is24h = timeFormat === 'HH:mm';

    const { control, reset, setValue } = useForm();
    const dispatch = useDispatch();

    const [state, setState] = useState({ startTime: null, endTime: null });
    const [validation, setValidations] = useState({ startTime: '', endTime: '' });

    // editTimeData.checkIn/checkOut are UTC ISO strings from the API;
    // convert to the viewer's local timezone (DST-safe) before display.
    useEffect(() => {
        if (!editTimeData?.checkIn) return;

        const startTimeDayJs = utcToLocal(editTimeData.checkIn);
        const endTimeDayJs = editTimeData?.checkOut
            ? utcToLocal(editTimeData.checkOut)
            : null;

        reset({ startTime: startTimeDayJs, endTime: endTimeDayJs });
        setState({ startTime: startTimeDayJs, endTime: endTimeDayJs });
    }, [editTimeData, reset]);

    // The local calendar date this entry belongs to — derived from the
    // existing (local-converted) check-in when editing, or the row's date
    // when clocking in fresh. Used to build full local datetimes from the
    // time-only values the TimePicker returns.
    const localDateStr = editTimeData?.checkIn
        ? (utcToLocal(editTimeData.checkIn)?.format('YYYY-MM-DD') || editTimeData?.date || dayjs().format('YYYY-MM-DD'))
        : (editTimeData?.date || dayjs().format('YYYY-MM-DD'));

    const closeDrawerHandler = () => {
        reset();
        setState({ startTime: null, endTime: null });
        setValidations({ startTime: '', endTime: '' });
        setDrawerStates({ ...drawerStates, checkoutTimeDrawer: false });
    };

    const editTimeMutation = useMutation(async (data) => {
        // data.startTime/endTime are local wall-clock dayjs values (see
        // handleTimeChange) — convert to UTC before sending to the API.
        const checkIn = localToUtc(data.startTime);
        const checkOut = data.endTime ? localToUtc(data.endTime) : null;
        if (!checkIn) {
            throw new Error('Invalid check-in time');
        }
        const payload = { checkIn, shiftUserId: editTimeData?.id };
        if (checkOut) payload.checkOut = checkOut;
        let res = await ApiCaller.Patch(`/attendance`, payload);
        return res;
    }, {
        onSuccess: (res) => {
            setState({ startTime: null, endTime: null });
            setDrawerStates({ ...drawerStates, checkoutTimeDrawer: false });
            // v2: prefer the onAfterSave callback
            if (typeof onAfterSave === 'function') {
                onAfterSave();
            } else {
                // legacy fallback
                dispatch(AttendenceManagementAction.GetAttendence({ filterStates }));
                setFilterStates({ ...filterStates });
            }
            Utils.showMessage('success', res?.data?.message);
        },
        onError: (error) => {
            Utils.showMessage('error', error?.response?.data?.message || error?.message);
        }
    });

    const editTimeHandler = (event) => {
        event.preventDefault();
        if (!state?.startTime) {
            setValidations({ startTime: 'Check-In Time is required', endTime: validation.endTime });
            return;
        }
        setValidations({ startTime: '', endTime: '' });
        editTimeMutation.mutate(state);
    };

    // Build TimePicker format and props based on timeFormat setting
    const pickerFormat = is24h ? 'HH:mm' : 'h:mm A';
    const use12Hours = !is24h;

    const handleTimeChange = (key, time) => {
        if (!time) {
            setState((prev) => ({ ...prev, [key]: null }));
            return;
        }
        // Combine the picked (local) time-of-day with the entry's local
        // calendar date — localDateStr is already timezone-converted, so we
        // must not re-derive it from raw UTC strings here.
        let newVal = dayjs(`${localDateStr} ${time.format('HH:mm')}`, 'YYYY-MM-DD HH:mm');
        // Overnight shift: a check-out at or before the check-in belongs to the
        // next calendar day (9 PM → 1 AM). Without this it would be stored on the
        // check-in's date and read back as negative worked hours.
        if (key === 'endTime' && state?.startTime && !newVal.isAfter(state.startTime)) {
            newVal = newVal.add(1, 'day');
        }
        setState((prev) => ({ ...prev, [key]: newVal }));
        setValue(key, time.format('HH:mm'));
    };

    return (
        <div>
            <BasicDrawer
                openDrawer={drawerStates?.checkoutTimeDrawer}
                onClose={closeDrawerHandler}
                width={300}
                title={'Edit Time'}
                children={
                    <form>
                        <Row gutter={[16, 32]} className='mb-5'>
                            <Col lg={24} md={24} sm={24} xs={24}>
                                <Controller
                                    name='startTime'
                                    control={control}
                                    render={() => (
                                        <div className='flex flex-col'>
                                            <label style={{ color: Colors.LabelColor }} className='font-normal'>
                                                Check-In Time
                                            </label>
                                            <ConfigProvider
                                                theme={{
                                                    components: {
                                                        DatePicker: {
                                                            optionSelectedBg: colorPrimary,
                                                            optionActiveBg: Colors.LightGray,
                                                            optionSelectedColor: Colors.White,
                                                        },
                                                    },
                                                }}
                                            >
                                                <TimePicker
                                                    value={state?.startTime}
                                                    use12Hours={use12Hours}
                                                    format={pickerFormat}
                                                    onChange={(time) =>
                                                        handleTimeChange('startTime', time)
                                                    }
                                                    className='outline-none py-1 rounded-md h-9 w-full'
                                                />
                                            </ConfigProvider>
                                            {validation?.startTime && (
                                                <p style={{ color: Colors.Error }}>{validation.startTime}</p>
                                            )}
                                        </div>
                                    )}
                                />
                            </Col>
                        </Row>

                        <Row gutter={[16, 32]} className='mb-6'>
                            <Col lg={24} md={24} sm={24} xs={24}>
                                <Controller
                                    name='endTime'
                                    control={control}
                                    render={() => (
                                        <div className='flex flex-col'>
                                            <label style={{ color: Colors.LabelColor }} className='font-normal'>
                                                Check-Out Time
                                                <span style={{ color: '#aaa', fontSize: 11, marginLeft: 4 }}>(optional)</span>
                                            </label>
                                            <ConfigProvider
                                                theme={{
                                                    components: {
                                                        DatePicker: {
                                                            optionSelectedBg: colorPrimary,
                                                            optionActiveBg: Colors.LightGray,
                                                            optionSelectedColor: Colors.White,
                                                        },
                                                    },
                                                }}
                                            >
                                                <TimePicker
                                                    value={state?.endTime}
                                                    use12Hours={use12Hours}
                                                    format={pickerFormat}
                                                    onChange={(time) =>
                                                        handleTimeChange('endTime', time)
                                                    }
                                                    className='outline-none py-1 rounded-md h-9 w-full'
                                                />
                                            </ConfigProvider>
                                            {/* the picker only shows a time, so spell out the day it lands on */}
                                            {state?.startTime && state?.endTime &&
                                                !state.endTime.isSame(state.startTime, 'day') && (
                                                    <p style={{ color: '#d46b08', fontSize: 11, marginTop: 4 }}>
                                                        Overnight shift — saved on{' '}
                                                        <strong>{state.endTime.format('ddd, MMM D')}</strong> (next day).
                                                    </p>
                                                )}
                                            {validation?.endTime && (
                                                <p style={{ color: Colors.Error }}>{validation.endTime}</p>
                                            )}
                                        </div>
                                    )}
                                />
                            </Col>
                        </Row>

                        <Row className='w-full my-6'>
                            <Col lg={24}>
                                <Button.Basic
                                    loader={editTimeMutation.isLoading}
                                    type={'submit'}
                                    onClick={editTimeHandler}
                                    size={'large'}
                                    className={'rounded-md px-3 w-full'}
                                    style={{ backgroundColor: colorPrimary, color: 'white' }}
                                    text={'Save Time'}
                                />
                            </Col>
                        </Row>
                    </form>
                }
            />
        </div>
    );
};

export default EditTimeDrawer;
